import React, { useState, useEffect, useCallback } from "react";

// ──────────────────────────────────────────────
// AdminSubdivision — #admin 결정표(국내세분 소호) 관리 (W3)
//   좌: 규칙 80건 목록(상태·버전·의견 수) + 신뢰 지표(개정·의견 통계)
//   우: 규칙 JSON 편집 + 상태 + 개정 사유(필수) → PUT(버전+1·이력·즉시 반영) + 이력.
//   인증 = admin_token. API: /api/admin/subdivision*
//   개정 권한=관리자(관세사) 단일 — 의견은 증거이지 권한이 아니다.
// ──────────────────────────────────────────────

const authHeaders = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtHs6 = (h) => (h && h.length === 6 ? `${h.slice(0, 4)}.${h.slice(4)}` : h);

export default function AdminSubdivision({ API_BASE_URL = "" }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null); // hs6
  const [detail, setDetail] = useState(null);
  const [ruleText, setRuleText] = useState("");
  const [status, setStatus] = useState("confirmed");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadList = useCallback(() => {
    fetch(`${API_BASE_URL}/api/admin/subdivision`, { headers: authHeaders() })
      .then((r) => (r.status === 401 ? Promise.reject("401") : r.json()))
      .then(setData)
      .catch((e) =>
        setData({
          rules: [],
          error:
            e === "401"
              ? "관리자 인증이 만료되었습니다. 다시 로그인해주세요."
              : "목록을 불러오지 못했습니다.",
        }),
      );
  }, [API_BASE_URL]);
  useEffect(loadList, [loadList]);

  const loadDetail = useCallback(
    (hs6) => {
      setSel(hs6);
      setDetail(null);
      setMsg(null);
      setReason("");
      fetch(`${API_BASE_URL}/api/admin/subdivision/${hs6}`, { headers: authHeaders() })
        .then((r) => r.json())
        .then((d) => {
          setDetail(d);
          if (d.rule) {
            setStatus(d.rule.status || "confirmed");
            try {
              setRuleText(JSON.stringify(JSON.parse(d.rule.rule), null, 2));
            } catch (e) {
              setRuleText(d.rule.rule || "");
            }
          }
        })
        .catch(() => setDetail({ error: "불러오기 실패" }));
    },
    [API_BASE_URL],
  );

  const save = async () => {
    if (!reason.trim()) return setMsg({ err: "개정 사유(reason)는 필수입니다." });
    let rule;
    try {
      rule = JSON.parse(ruleText);
    } catch (e) {
      return setMsg({ err: `rule JSON 파싱 실패: ${e.message}` });
    }
    if (rule.hs6 !== sel) return setMsg({ err: `rule.hs6(${rule.hs6})가 선택된 ${sel}와 다릅니다.` });
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/subdivision/${sel}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ rule, status, reason }),
      });
      const d = await r.json();
      if (d.success) {
        setMsg({ ok: "개정 완료 — 판정에 즉시 반영됩니다(버전 +1·이력 기록)." });
        loadDetail(sel);
        loadList();
      } else setMsg({ err: d.error || "개정 실패" });
    } catch (e) {
      setMsg({ err: "요청 실패" });
    } finally {
      setBusy(false);
    }
  };

  const stats = data?.stats;
  return (
    <div style={{ display: "flex", gap: 16, height: "calc(100vh - 120px)", padding: 12 }}>
      {/* 좌: 목록 */}
      <div style={{ width: 360, minWidth: 300, overflow: "auto", borderRight: "1px solid #eee", paddingRight: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, margin: "4px 0 6px" }}>
          결정표 (소호 세분) {data ? `${data.rules.length}건` : "…"}
        </div>
        {stats && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.6 }}>
            확정 {stats.confirmed} · 잠정 {stats.provisional} · 개정된 규칙 {stats.revised} ·
            품목 의견 {stats.opinions}건
            <div style={{ color: "#9ca3af" }}>
              답은 잠정 — 의견·반례 검토로 개정하며, 개정은 버전·사유·이력이 남습니다
            </div>
          </div>
        )}
        {data?.error && <div style={{ color: "#c0392b", fontSize: 13, padding: 8 }}>{data.error}</div>}
        {/* 검수 대기(provisional)를 위로, 그 안에서 신고물량 내림차순 — 승격 순서 그대로 */}
        {[...(data?.rules || [])]
          .sort((a, b) => {
            const pa = a.status === "provisional" ? 0 : 1;
            const pb = b.status === "provisional" ? 0 : 1;
            if (pa !== pb) return pa - pb;
            return Number(b.volume || 0) - Number(a.volume || 0);
          })
          .map((r) => (
          <div
            key={r.hs6}
            onClick={() => loadDetail(r.hs6)}
            title={
              r.review_tier
                ? `검수 계층 ${r.review_tier} · diff ${r.diff_changed}/${r.diff_samples}${
                    r.volume ? ` · 신고물량 ${Number(r.volume).toLocaleString()}` : ""
                  }`
                : undefined
            }
            style={{
              padding: "9px 10px",
              borderBottom: "1px solid #f2f2f4",
              cursor: "pointer",
              background: sel === r.hs6 ? "#eef2ff" : "transparent",
              borderRadius: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <code style={{ fontSize: 13.5, fontWeight: 700 }}>{fmtHs6(r.hs6)}</code>
              {r.status === "confirmed" ? (
                <span style={{ ...tag, background: "#e6f4ea", color: "#137333" }}>확정</span>
              ) : (
                <span style={{ ...tag, background: "#fff4e5", color: "#b06000" }}>잠정</span>
              )}
              {Number(r.version) > 1 && (
                <span style={{ ...tag, background: "#e8f0fe", color: "#1a56b0" }}>v{r.version}</span>
              )}
              {Number(r.opinions) > 0 && (
                <span
                  style={{ ...tag, background: "#fde8e8", color: "#b00020" }}
                  title="공개 품목 의견 — 검토 후 개정 또는 회신"
                >
                  의견 {r.opinions}
                </span>
              )}
              {/* 검수 보조 — 계층 1=경검수(diff 변화 0·R7 기계 증명), 2=건별 */}
              {r.review_tier === 1 && (
                <span
                  style={{ ...tag, background: "#e6f4ea", color: "#137333" }}
                  title="경검수 — v1/v2 판정 변화 0, 수치 축은 R7이 형제 무모순을 증명"
                >
                  경검수
                </span>
              )}
              {r.review_tier === 2 && (
                <span
                  style={{ ...tag, background: "#fff4e5", color: "#b06000" }}
                  title="건별 검수 — 판정이 달라졌거나 계층·술어가 신설된 규칙"
                >
                  건별
                </span>
              )}
              {Number(r.diff_changed) > 0 && (
                <span
                  style={{ ...tag, background: "#e8f0fe", color: "#1a56b0" }}
                  title={`결정례 표본 ${r.diff_samples}건 중 ${r.diff_changed}건에서 판정이 달라졌다`}
                >
                  diff {r.diff_changed}
                </span>
              )}
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af" }}>{r.updated_at}</span>
            </div>
            {r.legal_basis && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#6b7280",
                  marginTop: 3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.legal_basis}
              </div>
            )}
          </div>
          ))}
      </div>

      {/* 우: 상세·개정 */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {!sel ? (
          <div style={{ color: "#9ca3af", fontSize: 13, padding: 24 }}>
            좌측에서 규칙을 선택하세요. 개정하면 버전이 오르고 사유가 이력에 남으며 판정에 즉시
            반영됩니다.
          </div>
        ) : !detail ? (
          <div style={{ color: "#9ca3af", fontSize: 13, padding: 24 }}>불러오는 중…</div>
        ) : detail.error ? (
          <div style={{ color: "#c0392b", fontSize: 13, padding: 24 }}>{detail.error}</div>
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <code style={{ fontSize: 17, fontWeight: 800 }}>{fmtHs6(sel)}</code>
              <span style={{ fontSize: 12.5, color: "#6b7280" }}>
                v{detail.rule.version} · {detail.rule.updated_by} · {detail.rule.updated_at}
              </span>
            </div>

            {/* ★검수의 본체 — 원문 문언 ↔ 변환된 조건 대조표. JSON 은 필요할 때만 본다. */}
            {detail.review_aid && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, marginBottom: 6, color: "#374151" }}>
                  <b>
                    검수 계층 {detail.review_aid.tier}
                    {detail.review_aid.tier === 1 ? " (경검수)" : " (건별)"}
                  </b>
                  {" · "}
                  결정례 표본 {detail.review_aid.diff_samples}건 중 판정이 달라진 케이스{" "}
                  <b style={{ color: detail.review_aid.diff_changed ? "#b06000" : "#137333" }}>
                    {detail.review_aid.diff_changed}건
                  </b>
                  {detail.review_aid.volume > 0 &&
                    ` · 신고물량 ${Number(detail.review_aid.volume).toLocaleString()}`}
                </div>

                {detail.review_aid.diff_cases?.length > 0 && (
                  <div
                    style={{
                      fontSize: 12,
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: 6,
                      padding: "8px 10px",
                      marginBottom: 8,
                      lineHeight: 1.6,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 3 }}>
                      v1 → v2 달라진 케이스 (실제 결정례)
                    </div>
                    {detail.review_aid.diff_cases.map((c, i) => (
                      <div key={i} style={{ color: "#374151" }}>
                        · {c}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                        <th style={th}>코드</th>
                        <th style={th}>원문 문언 (관세율표)</th>
                        <th style={th}>변환된 조건</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.review_aid.map?.map((m, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #f2f2f4" }}>
                          <td style={{ ...td, paddingLeft: 6 + m.depth * 12, whiteSpace: "nowrap" }}>
                            <code style={{ fontWeight: m.leaf ? 700 : 400, color: m.leaf ? "#16151a" : "#6b7280" }}>
                              {m.code}
                            </code>
                          </td>
                          <td style={td}>{m.source_text}</td>
                          <td style={{ ...td, color: m.encoded.startsWith("(") ? "#b06000" : "#1a56b0" }}>
                            {m.encoded}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 4 }}>
                  들여쓰기 = 계층 · 굵은 코드 = 잎(10자리) · 주황 = 조건 없는 잔여(형제 배제 필요)
                </div>
              </div>
            )}

            <textarea
              value={ruleText}
              onChange={(e) => setRuleText(e.target.value)}
              spellCheck={false}
              rows={22}
              style={{
                width: "100%",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                lineHeight: 1.5,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 10,
                boxSizing: "border-box",
              }}
            />

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ fontSize: 13, padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6 }}
              >
                <option value="confirmed">confirmed (판정에 사용)</option>
                <option value="provisional">provisional (검수 대기 — 판정 제외)</option>
              </select>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="개정 사유 (필수 — 이력에 기록됩니다)"
                style={{
                  flex: 1,
                  minWidth: 240,
                  fontSize: 13,
                  padding: "6px 10px",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                }}
              />
              <button
                onClick={save}
                disabled={busy}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "7px 16px",
                  background: "#1b1a20",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: busy ? "default" : "pointer",
                }}
              >
                {busy ? "개정 중…" : "개정 저장"}
              </button>
            </div>
            {msg?.err && <div style={{ color: "#c0392b", fontSize: 13, marginTop: 8 }}>{msg.err}</div>}
            {msg?.ok && <div style={{ color: "#137333", fontSize: 13, marginTop: 8 }}>{msg.ok}</div>}

            <div style={{ fontSize: 13.5, fontWeight: 700, margin: "18px 0 6px" }}>개정 이력</div>
            {(detail.history || []).map((h, i) => (
              <div
                key={i}
                style={{
                  fontSize: 12.5,
                  padding: "7px 10px",
                  borderLeft: "3px solid #e5e7eb",
                  marginBottom: 6,
                  background: "#fafafa",
                  borderRadius: "0 6px 6px 0",
                }}
              >
                <b>v{h.version}</b> · {h.status} · {h.changed_by} · {h.changed_at}
                <div style={{ color: "#374151", marginTop: 2 }}>{h.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const tag = {
  fontSize: 11,
  fontWeight: 700,
  padding: "1px 7px",
  borderRadius: 10,
  whiteSpace: "nowrap",
};

const th = {
  textAlign: "left",
  fontSize: 11.5,
  fontWeight: 700,
  color: "#6b7280",
  padding: "6px 8px",
  borderBottom: "1px solid #e5e7eb",
};

const td = { padding: "5px 8px", verticalAlign: "top", lineHeight: 1.5 };
